import React from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, Typography } from '@mui/material';
import { COPY, pageFormatLabel } from '../presentationCopy';
import { PRES, TYPE } from '../chrome';
import { PresentationTemplate } from '../types';
import { kitsForLayoutTarget } from './kitLayoutTargets';

export type AddLayoutToKitMode = 'add' | 'copy' | 'move';

interface AddLayoutToKitDialogProps {
  open: boolean;
  mode: AddLayoutToKitMode;
  templates: PresentationTemplate[];
  pageFormat?: string | null;
  excludeTemplateId?: string | null;
  name: string;
  onNameChange: (name: string) => void;
  kitId: string;
  onKitChange: (kitId: string) => void;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (openAfter?: boolean) => void;
}

const AddLayoutToKitDialog: React.FC<AddLayoutToKitDialogProps> = ({
  open,
  mode,
  templates,
  pageFormat,
  excludeTemplateId,
  name,
  onNameChange,
  kitId,
  onKitChange,
  busy,
  error,
  onCancel,
  onConfirm,
}) => {
  const kits = kitsForLayoutTarget(templates, pageFormat, excludeTemplateId);
  const title = mode === 'add'
    ? COPY.addAsTemplateTitle
    : mode === 'copy'
      ? COPY.copyLayoutToKitTitle
      : COPY.moveLayoutToKitTitle;
  const confirmLabel = mode === 'add'
    ? COPY.addAsTemplateSubmit
    : mode === 'copy'
      ? COPY.copyLayoutSubmit
      : COPY.moveLayoutSubmit;
  const canSubmit = Boolean(kitId && kits.some((kit) => kit._id === kitId) && !busy);

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        {mode === 'add' && (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.addAsTemplateHelp}
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {!kits.length ? (
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.addAsTemplateNoKits}
          </Typography>
        ) : (
          <>
            <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textSecondary }}>
              {COPY.addAsTemplateKit}
            </Typography>
            <Select
              size="small"
              value={kitId}
              onChange={(event) => onKitChange(event.target.value)}
              disabled={busy}
              displayEmpty
              inputProps={{ 'aria-label': COPY.addAsTemplateKit }}
            >
              {kits.map((template) => (
                <MenuItem key={template._id} value={template._id}>
                  {template.name} · {pageFormatLabel(template.pageFormat)}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
        <TextField
          autoFocus
          fullWidth
          label={COPY.addAsTemplateName}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onCancel} sx={{ textTransform: 'none' }}>{COPY.cancel}</Button>
        {mode === 'add' && (
          <Button
            disabled={!canSubmit}
            onClick={() => onConfirm(true)}
            sx={{ textTransform: 'none' }}
          >
            {COPY.addAsTemplateSubmitOpen}
          </Button>
        )}
        <Button
          disabled={!canSubmit}
          variant="contained"
          sx={{ bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none' }}
          onClick={() => onConfirm(false)}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLayoutToKitDialog;
